/**
 * v3.15-1167 — Ecosystem hierarchy graphic.
 *
 * Visualizes how Prestigious Companies → Prestigious Hub → (Concierge + Ghost)
 * → CreditForward360 / EasyQuickFund / Business Funding fit together.
 *
 * Used on /platform, /about, /presentation. Pure CSS, no chart libs.
 */
import { Link } from 'react-router-dom';
import { Users, ExternalLink, Building2 } from 'lucide-react';
import GhostLogo from './GhostLogo';

const Node = ({ label, sublabel, tone = 'slate', className = '', testId }) => {
  const tones = {
    slate:   'bg-white border-slate-200 text-slate-900',
    primary: 'bg-primary border-primary text-white',
    gold:    'bg-gradient-gold border-amber-400 text-slate-900',
    dark:    'bg-slate-900 border-slate-700 text-white',
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-lg border px-4 py-3 text-center shadow-sm ${tones[tone]} ${className}`}
    >
      <p className="font-heading font-bold text-sm sm:text-base leading-tight">{label}</p>
      {sublabel && <p className="text-[10px] uppercase tracking-wider mt-1 opacity-80">{sublabel}</p>}
    </div>
  );
};

const Connector = ({ vertical = true }) => (
  <div
    aria-hidden
    className={vertical ? 'mx-auto w-px h-6 bg-gradient-to-b from-gold/0 via-gold to-gold/0' : 'h-px w-full bg-gradient-to-r from-gold/0 via-gold to-gold/0'}
  />
);

const EcosystemGraphic = ({ testId = 'ecosystem-graphic' }) => (
  <section className="bg-white border border-slate-100 rounded-xl shadow-soft p-6 sm:p-10" data-testid={testId}>
    <div className="max-w-3xl mx-auto">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold/80 text-center mb-1">The Ecosystem</p>
      <h3 className="font-heading text-2xl sm:text-3xl font-bold text-primary text-center mb-8">
        How it all fits together.
      </h3>

      {/* Tier 1 — Parent network */}
      <Node
        label="Prestigious Companies"
        sublabel="Parent Network"
        tone="dark"
        className="max-w-xs mx-auto"
        testId="ecosystem-node-parent"
      />
      <Connector />

      {/* Tier 2 — The platform */}
      <Node
        label="Prestigious Hub"
        sublabel="The Financial Concierge Platform"
        tone="primary"
        className="max-w-md mx-auto"
        testId="ecosystem-node-hub"
      />
      <Connector />

      {/* Tier 3 — Human + AI */}
      <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center" data-testid="ecosystem-node-concierge">
          <Users className="h-5 w-5 mx-auto text-primary mb-2" />
          <p className="font-heading font-bold text-sm text-slate-900">Application Concierge</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">Human Layer</p>
        </div>
        <div className="rounded-lg border border-gold/40 bg-slate-900 text-white p-4 text-center" data-testid="ecosystem-node-ghost">
          <GhostLogo size="sm" className="mx-auto mb-2" testId="ecosystem-node-ghost-logo" />
          <p className="font-heading font-bold text-sm">Ghost<sup className="text-[7px]">™</sup></p>
          <p className="text-[10px] uppercase tracking-wider text-gold/80 mt-1">AI Layer</p>
        </div>
      </div>
      <Connector />

      {/* Tier 4 — Three products */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <a
          href="https://easyquickfund.com"
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-lg border border-slate-200 bg-white p-4 text-center hover:border-primary/30 hover:shadow-soft transition-all"
          data-testid="ecosystem-node-eqf"
        >
          <p className="font-heading font-bold text-sm text-primary inline-flex items-center justify-center gap-1">
            EasyQuickFund <ExternalLink className="h-3 w-3 text-slate-400" />
          </p>
          <p className="text-[11px] text-slate-600 mt-1 leading-snug">Consumer Funding<br />up to $100K</p>
        </a>
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-center" data-testid="ecosystem-node-business">
          <Building2 className="h-4 w-4 mx-auto text-primary mb-1" />
          <p className="font-heading font-bold text-sm text-primary">Business Funding</p>
          <p className="text-[11px] text-slate-600 mt-1 leading-snug">Working capital,<br />equipment, expansion</p>
        </div>
        <a
          href="https://creditforward360.com"
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-lg border border-slate-200 bg-white p-4 text-center hover:border-primary/30 hover:shadow-soft transition-all"
          data-testid="ecosystem-node-cf360"
        >
          <p className="font-heading font-bold text-sm text-primary inline-flex items-center justify-center gap-1">
            CreditForward360 <ExternalLink className="h-3 w-3 text-slate-400" />
          </p>
          <p className="text-[11px] text-slate-600 mt-1 leading-snug">Today&rsquo;s decline →<br />tomorrow&rsquo;s approval</p>
        </a>
      </div>

      <p className="mt-8 text-center text-xs text-slate-500 max-w-xl mx-auto leading-relaxed">
        One platform. One team. Three financial solutions — orchestrated by a human Concierge and accelerated by Ghost Intelligence™.{' '}
        <Link to="/ghost" className="text-primary underline-offset-2 hover:underline" data-testid="ecosystem-ghost-link">
          Learn about Ghost →
        </Link>
      </p>
    </div>
  </section>
);

export default EcosystemGraphic;
